import React, { useCallback, useRef, useState } from 'react';
import { useI18n } from '../../i18n/I18nContext';
import { trackEvent } from '../../analytics';
import './RequestForm.css';

interface FormData {
  name: string;
  email: string;
  category: string;
  userType: 'personal' | 'business' | '';
  description: string;
  style: string;
  format: string;
}

type FormErrors = Partial<Record<keyof FormData, string>>;
type ErrorMessages = Record<keyof FormData, string>;

const INITIAL_FORM: FormData = {
  name: '', email: '', category: '', userType: '', description: '', style: '', format: '',
};

function validate(data: FormData, messages: ErrorMessages): FormErrors {
  const errors: FormErrors = {};
  if (data.name.trim().length < 2) errors.name = messages.name;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) errors.email = messages.email;
  if (!data.category) errors.category = messages.category;
  if (!data.userType) errors.userType = messages.userType;
  if (data.description.trim().length < 20) errors.description = messages.description;
  if (!data.style) errors.style = messages.style;
  if (!data.format) errors.format = messages.format;
  return errors;
}

const RequestForm: React.FC = () => {
  const { content } = useI18n();
  const copy = content.request;
  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Set<keyof FormData>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  const formStarted = useRef(false);

  const handleChange = useCallback((
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const field = event.target.name as keyof FormData;
    setForm(current => ({ ...current, [field]: event.target.value }));
    setTouched(current => new Set(current).add(field));
  }, []);

  const setUserType = (userType: 'personal' | 'business') => {
    setForm(current => ({ ...current, userType }));
    setTouched(current => new Set(current).add('userType'));
  };

  const handleBlur = (field: keyof FormData) => {
    setTouched(current => new Set(current).add(field));
    setErrors(validate(form, copy.errors));
  };

  const optionLabel = (options: Array<{ id: string; label: string }>, id: string) =>
    options.find(option => option.id === id)?.label || id;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(new Set(Object.keys(form) as Array<keyof FormData>));
    const validationErrors = validate(form, copy.errors);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) {
      trackEvent('form_validation_error', {
        form_name: 'asset_set_request',
        error_count: Object.keys(validationErrors).length,
        asset_category: form.category || 'not_selected',
      });
      return;
    }

    const category = optionLabel(copy.categories, form.category);
    const style = optionLabel(copy.styles, form.style);
    const format = optionLabel(copy.formats, form.format);
    const use = form.userType === 'business' ? copy.fields.business : copy.fields.personal;
    const subject = encodeURIComponent(`${copy.email.subject}: ${category}`);
    const body = encodeURIComponent([
      `${copy.email.name}: ${form.name}`,
      `${copy.fields.email}: ${form.email}`,
      `${copy.email.use}: ${use}`,
      `${copy.email.category}: ${category}`,
      `${copy.email.style}: ${style}`,
      `${copy.email.format}: ${format}`,
      '', `${copy.email.description}:`, form.description,
    ].join('\n'));

    trackEvent('generate_lead', {
      form_name: 'asset_set_request',
      asset_category: form.category,
      user_type: form.userType,
      requested_format: form.format,
    });
    window.location.href = `mailto:hello@assetfactory.app?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  const fieldError = (field: keyof FormData) => touched.has(field) ? errors[field] : undefined;

  if (submitted) {
    return (
      <section className="request-section section" aria-live="polite">
        <div className="container">
          <div className="request__success">
            <span>{copy.ready}</span><h2>{copy.successTitle}</h2><p>{copy.successText}</p>
            <button className="btn btn-outline" onClick={() => {
              setForm(INITIAL_FORM); setErrors({}); setTouched(new Set()); setSubmitted(false); formStarted.current = false;
            }}>{copy.another}</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="request-section section" aria-label={copy.aria}>
      <div className="container request__layout">
        <aside className="request__info">
          <h2>{copy.infoTitle}</h2>
          <ol>
            {copy.info.map((item, index) => (
              <li key={item.title}><span>{index + 1}</span><p><strong>{item.title}</strong>{item.text}</p></li>
            ))}
          </ol>
          <p className="request__privacy">{copy.privacy}</p>
        </aside>

        <form className="request__form" onSubmit={handleSubmit} onFocus={() => {
          if (!formStarted.current) {
            formStarted.current = true;
            trackEvent('form_start', { form_name: 'asset_set_request' });
          }
        }} noValidate>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label" htmlFor="name">{copy.fields.name}</label>
              <input id="name" name="name" className={`form-input${fieldError('name') ? ' error' : ''}`}
                value={form.name} onChange={handleChange} onBlur={() => handleBlur('name')}
                autoComplete="name" aria-invalid={Boolean(fieldError('name'))} />
              {fieldError('name') && <span className="form-error">{fieldError('name')}</span>}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="email">{copy.fields.email}</label>
              <input id="email" name="email" type="email" className={`form-input${fieldError('email') ? ' error' : ''}`}
                value={form.email} onChange={handleChange} onBlur={() => handleBlur('email')}
                autoComplete="email" aria-invalid={Boolean(fieldError('email'))} />
              {fieldError('email') && <span className="form-error">{fieldError('email')}</span>}
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label className="form-label" htmlFor="category">{copy.fields.category}</label>
              <select id="category" name="category" className={`form-input${fieldError('category') ? ' error' : ''}`}
                value={form.category} onChange={handleChange} onBlur={() => handleBlur('category')}>
                <option value="">{copy.placeholders.category}</option>
                {copy.categories.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
              </select>
              {fieldError('category') && <span className="form-error">{fieldError('category')}</span>}
            </div>
            <div className="form-group">
              <span className="form-label">{copy.fields.use}</span>
              <div className={`request__type-toggle${fieldError('userType') ? ' error' : ''}`}>
                <button type="button" className={form.userType === 'personal' ? 'active' : ''}
                  onClick={() => setUserType('personal')} aria-pressed={form.userType === 'personal'}>{copy.fields.personal}</button>
                <button type="button" className={form.userType === 'business' ? 'active' : ''}
                  onClick={() => setUserType('business')} aria-pressed={form.userType === 'business'}>{copy.fields.business}</button>
              </div>
              {fieldError('userType') && <span className="form-error">{fieldError('userType')}</span>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="description">
              {copy.fields.description} <span className="request__char-count">{form.description.length} {copy.fields.characters}</span>
            </label>
            <textarea id="description" name="description"
              className={`form-input request__textarea${fieldError('description') ? ' error' : ''}`}
              placeholder={copy.placeholders.description} value={form.description}
              onChange={handleChange} onBlur={() => handleBlur('description')} rows={6} />
            {fieldError('description') && <span className="form-error">{fieldError('description')}</span>}
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label className="form-label" htmlFor="style">{copy.fields.style}</label>
              <select id="style" name="style" className={`form-input${fieldError('style') ? ' error' : ''}`}
                value={form.style} onChange={handleChange} onBlur={() => handleBlur('style')}>
                <option value="">{copy.placeholders.style}</option>
                {copy.styles.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
              </select>
              {fieldError('style') && <span className="form-error">{fieldError('style')}</span>}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="format">{copy.fields.format}</label>
              <select id="format" name="format" className={`form-input${fieldError('format') ? ' error' : ''}`}
                value={form.format} onChange={handleChange} onBlur={() => handleBlur('format')}>
                <option value="">{copy.placeholders.format}</option>
                {copy.formats.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
              </select>
              {fieldError('format') && <span className="form-error">{fieldError('format')}</span>}
            </div>
          </div>
          <button type="submit" className="btn btn-primary btn-lg request__submit">{copy.submit}</button>
        </form>
      </div>
    </section>
  );
};

export default RequestForm;
